import type {CmdKey} from "@milkdown/core";
import CrossIcon from "@atlaskit/icon/core/cross";
import EditIcon from "@atlaskit/icon/core/edit";
import CheckCircleIcon from "@atlaskit/icon/core/check-circle";
import {Editor, defaultValueCtx, rootCtx, editorViewCtx} from "@milkdown/core";
import {history} from "@milkdown/plugin-history";
import {listener, listenerCtx} from "@milkdown/plugin-listener";
import {
    toggleStrongCommand,
    toggleEmphasisCommand,
    wrapInHeadingCommand,
    wrapInBulletListCommand,
    wrapInOrderedListCommand,
    commonmark,
} from "@milkdown/preset-commonmark";
import {
    gfm,
} from "@milkdown/preset-gfm";
import {Milkdown, MilkdownProvider, useEditor} from "@milkdown/react";
import {callCommand} from "@milkdown/utils";
import {micromark} from "micromark";
import {gfm as gfmExtension, gfmHtml} from "micromark-extension-gfm";
import {
    ListOl,
    ListUl,
    TypeBold,
    TypeH1,
    TypeH2,
    TypeItalic,
} from "react-bootstrap-icons";
import {useEffect, useState} from "react";
import {twJoin} from "tailwind-merge";
import {
    type ContentBlockDragHandleProps,
    EditableContentBlockWrapper,
    ReadOnlyContentBlockWrapper,
} from "~/components/btlActivityTemplateComponents/contentBlockWrapper";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

type ActiveFormats = {
    strong: boolean;
    emphasis: boolean;
    strikethrough: boolean;
    heading1: boolean;
    heading2: boolean;
    bulletList: boolean;
    orderedList: boolean;
    blockquote: boolean;
    codeBlock: boolean;
};

const DEFAULT_ACTIVE_FORMATS: ActiveFormats = {
    strong: false,
    emphasis: false,
    strikethrough: false,
    heading1: false,
    heading2: false,
    bulletList: false,
    orderedList: false,
    blockquote: false,
    codeBlock: false,
};

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call */
function detectActiveFormats(state: any): ActiveFormats {
    const {$from, from, to, empty} = state.selection;

    const isMarkOn = (name: string): boolean => {
        const mt = state.schema.marks[name];
        if (mt === undefined) {
            return false;
        }
        if (empty) {
            if (state.storedMarks !== null) {
                return mt.isInSet(state.storedMarks) !== undefined;
            }
            return mt.isInSet($from.marks()) !== undefined;
        }
        return state.doc.rangeHasMark(from, to, mt);
    };

    let heading1 = false;
    let heading2 = false;
    let bulletList = false;
    let orderedList = false;
    let blockquote = false;
    let codeBlock = false;

    for (let d = $from.depth; d >= 0; d--) {
        const node = $from.node(d);
        const name = node.type.name;
        if (name === "heading") {
            const level = (node.attrs as {level?: number}).level;
            if (level === 1) {heading1 = true;}
            if (level === 2) {heading2 = true;}
        } else if (name === "bullet_list") {
            bulletList = true;
        } else if (name === "ordered_list") {
            orderedList = true;
        } else if (name === "blockquote") {
            blockquote = true;
        } else if (name === "code_block") {
            codeBlock = true;
        }
    }

    return {
        strong: isMarkOn("strong"),
        emphasis: isMarkOn("emphasis"),
        strikethrough: isMarkOn("strike_through"),
        heading1, heading2, bulletList, orderedList,
        blockquote, codeBlock,
    };
}
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call */

const RICH_TEXT_SUBTITLE = "Use formatting, lists, and headings to describe this template.";

const PROSE_COMMON_STYLES =
    "prose prose-sm md:prose-base dark:prose-invert max-w-none min-w-0 w-full max-w-full text-ds-text break-words [overflow-wrap:anywhere]" +
    " [&_>*:first-child]:mt-0 [&_>*:last-child]:mb-0" +
    " [&_p]:my-[0.2em] [&_p]:leading-[1.5] [&_p]:break-words [&_p]:text-[14px] md:[&_p]:text-[15px]" +
    " [&_h1]:text-[1.5em] [&_h1]:my-[0.3em] [&_h1]:font-bold [&_h1]:leading-tight" +
    " [&_h2]:text-[1.25em] [&_h2]:my-[0.2em] [&_h2]:font-bold [&_h2]:leading-tight" +
    " [&_ul]:my-[0.2em] [&_ol]:my-[0.2em] [&_li]:my-[0.1em] [&_li]:leading-[1.5] [&_li]:text-[14px] md:[&_li]:text-[15px]" +
    " [&_blockquote]:border-l-4 [&_blockquote]:border-ds-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-2" +
    " [&_table]:border-collapse [&_table]:border [&_table]:border-ds-border [&_table]:table-fixed" +
    " [&_td]:border [&_td]:border-ds-border [&_td]:px-ds-space-100 [&_td]:py-ds-space-050" +
    " [&_th]:border [&_th]:border-ds-border [&_th]:px-ds-space-100 [&_th]:py-ds-space-050 [&_th]:font-semibold [&_th]:text-left [&_th]:bg-ds-surface-sunken";

const PROSE_PREVIEW_CLASS = PROSE_COMMON_STYLES;
const PROSE_EDITOR_CLASS = twJoin(PROSE_COMMON_STYLES, "[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[10rem]");

export type BtlActivityTemplateMarkdownSectionProps = {
    sectionId: Uuid;
    sectionTitle: string;
    value: string;
    setValue: React.Dispatch<React.SetStateAction<string>>;
    error: string | null;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
    mode?: "editable" | "readonly";
    onDragStart?: () => void;
    onDragEnd?: () => void;
    isDraggable?: boolean;
    dragHandleProps?: ContentBlockDragHandleProps;
    containerClassName?: string;
    showInlineEditToggle?: boolean;
    useContainer?: boolean;
    onRemoveBlock?: () => void;
    /** Notes-style composer: toolbar + editor only, no BTL drag/title chrome. */
    editorLayout?: "block" | "inline";
    toolbarLeadingContent?: React.ReactNode;
};

export function BtlActivityTemplateMarkdownSection(props: BtlActivityTemplateMarkdownSectionProps) {
    // Top-level provider for the entire component's life if needed,
    // though we use inner ones for mode resets.
    return <BtlActivityTemplateMarkdownSectionInner {...props} />;
}

function BtlActivityTemplateMarkdownSectionInner({
    sectionId: _sectionId,
    sectionTitle,
    value,
    setValue,
    error,
    setError,
    mode = "editable",
    onDragStart,
    onDragEnd,
    isDraggable: isDraggableProp = false,
    dragHandleProps,
    containerClassName,
    showInlineEditToggle = true,
    useContainer = true,
    onRemoveBlock,
    editorLayout = "block",
    toolbarLeadingContent,
}: BtlActivityTemplateMarkdownSectionProps) {
    const isDraggable = isDraggableProp ?? (dragHandleProps !== undefined);
    const [isEditing, setIsEditing] = useState(mode === "editable" && showInlineEditToggle === false);

    useEffect(() => {
        if (mode === "readonly") {
            setIsEditing(false);
            return;
        }
        if (showInlineEditToggle === false) {
            setIsEditing(true);
        }
    }, [mode, showInlineEditToggle]);

    const hasContent = value.trim() !== "";

    if (mode === "editable" && editorLayout === "inline") {
        return (
            <MilkdownProvider>
                <div className="grid min-w-0">
                    <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-ds-space-050 border-b border-ds-border px-ds-space-100 py-ds-space-075">
                        {toolbarLeadingContent}
                        <Toolbar value={value} setValue={setValue} />
                    </div>
                    <div className="px-ds-space-100 pt-ds-space-075 pb-ds-space-050">
                        <EditorContainer value={value} setValue={setValue} error={error} />
                    </div>
                </div>
            </MilkdownProvider>
        );
    }

    if (mode === "readonly") {
        const content = (
            <div
                dangerouslySetInnerHTML={{
                    __html: micromark(value, {
                        extensions: [gfmExtension()],
                        htmlExtensions: [gfmHtml()],
                        allowDangerousHtml: true,
                    }),
                }}
                className={PROSE_PREVIEW_CLASS}
            />
        );

        if (useContainer === false) {
            return (
                <div className="min-w-0 max-w-full overflow-x-hidden [word-break:break-word]">{content}</div>
            );
        }

        return (
            <div className={twJoin("w-full")}>
                <ReadOnlyContentBlockWrapper title={sectionTitle}>
                    {hasContent === false ? (
                        <p className="text-ds-font-body-small text-ds-text-subtle mb-ds-space-150">
                            {RICH_TEXT_SUBTITLE}
                        </p>
                    ) : null}
                    {content}
                </ReadOnlyContentBlockWrapper>
            </div>
        );
    }

    return (
        <MilkdownProvider key={isEditing ? "editing" : "preview"}>
            <div className={twJoin("w-full")}>
                <EditableContentBlockWrapper
                    title={sectionTitle}
                    setTitle={() => {}}
                    onDragStart={onDragStart ?? (() => {})}
                    onDragEnd={onDragEnd ?? (() => {})}
                    hideTitle={true}
                    headerLeadingContent={isEditing ? <Toolbar value={value} setValue={setValue} /> : undefined}
                    headerTrailingContent={
                        <div className="flex items-center gap-ds-space-100">
                            {onRemoveBlock !== undefined && (
                                <AnankeIconButton
                                    type="button"
                                    appearance="subtle"
                                    icon={CrossIcon}
                                    label="Remove text block"
                                    onClick={onRemoveBlock}
                                />
                            )}
                        </div>
                    }
                    additonalContent={showInlineEditToggle ? (
                        <AnankeIconButton
                            type="button"
                            appearance="subtle"
                            onClick={() => {
                                if (isEditing) {
                                    setError(null);
                                    setIsEditing(false);
                                } else {
                                    setIsEditing(true);
                                }
                            }}
                            icon={(iconProps) =>
                                isEditing ? (
                                    <CheckCircleIcon {...iconProps} label="Update" size="medium" />
                                ) : (
                                    <EditIcon {...iconProps} label="Edit" size="medium" />
                                )
                            }
                            label={isEditing ? "Update" : "Edit"}
                        />
                    ) : undefined}
                    additionalContentVariant="inline"
                    isDraggable={isDraggable}
                    dragHandleProps={dragHandleProps}
                    containerClassName={containerClassName}
                >
                    {isEditing === false && hasContent === false ? (
                        <p className="text-ds-font-body-small text-ds-text-subtle mb-ds-space-150">
                            {RICH_TEXT_SUBTITLE}
                        </p>
                    ) : null}
                    {isEditing || showInlineEditToggle === false ? (
                        <EditorContainer value={value} setValue={setValue} error={error} />
                    ) : (
                        <div className="min-w-0 max-w-full overflow-x-hidden [word-break:break-word]">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: micromark(value, {
                                        extensions: [gfmExtension()],
                                        htmlExtensions: [gfmHtml()],
                                        allowDangerousHtml: true,
                                    }),
                                }}
                                className={PROSE_PREVIEW_CLASS}
                            />
                        </div>
                    )}
                </EditableContentBlockWrapper>
            </div>
        </MilkdownProvider>
    );
}

function Toolbar({value, setValue}: {value: string; setValue: (v: string) => void}) {
    const [activeFormats, setActiveFormats] = useState<ActiveFormats>(DEFAULT_ACTIVE_FORMATS);

    const {get} = useEditor((root) =>
        Editor.make()
            .config((ctx) => {
                ctx.set(rootCtx, root);
                ctx.set(defaultValueCtx, value);
                ctx.get(listenerCtx)
                    .markdownUpdated((_ctx, markdown) => {
                        setValue(markdown);
                    })
                    .selectionUpdated((ctx) => {
                        setActiveFormats(detectActiveFormats(ctx.get(editorViewCtx).state));
                    });
            })
            .use(commonmark)
            .use(gfm)
            .use(history)
            .use(listener),
    );

    const run = <T,>(command: CmdKey<T>, payload?: T) => {
        get()?.action((ctx) => {
            callCommand(command, payload)(ctx);
            setActiveFormats(detectActiveFormats(ctx.get(editorViewCtx).state));
        });
    };

    return (
        <div className="flex items-center gap-ds-space-050">
            <ToolbarButton
                label="Heading 1"
                icon={<TypeH1 />}
                active={activeFormats.heading1}
                onClick={() => run(wrapInHeadingCommand.key, 1)}
            />
            <ToolbarButton
                label="Heading 2"
                icon={<TypeH2 />}
                active={activeFormats.heading2}
                onClick={() => run(wrapInHeadingCommand.key, 2)}
            />
            <ToolbarDivider />
            <ToolbarButton
                label="Bold"
                icon={<TypeBold />}
                active={activeFormats.strong}
                onClick={() => run(toggleStrongCommand.key)}
            />
            <ToolbarButton
                label="Italic"
                icon={<TypeItalic />}
                active={activeFormats.emphasis}
                onClick={() => run(toggleEmphasisCommand.key)}
            />
            <ToolbarDivider />
            <ToolbarButton
                label="Bullet List"
                icon={<ListUl />}
                active={activeFormats.bulletList}
                onClick={() => run(wrapInBulletListCommand.key)}
            />
            <ToolbarButton
                label="Numbered List"
                icon={<ListOl />}
                active={activeFormats.orderedList}
                onClick={() => run(wrapInOrderedListCommand.key)}
            />
        </div>
    );
}

function EditorContainer({value, setValue, error}: {value: string; setValue: (v: string) => void; error: string | null}) {
    // The Milkdown view itself doesn't need to redeclare the full editor if it's already in the Provider,
    // but useEditor is how we attach it to the DOM.
    // We already called useEditor in Toolbar, but we need another one or use the same root.
    // Actually, each useEditor creates a NEW editor instance.
    // To share the editor, we should have ONE useEditor call.

    return (
        <div className="grid grid-flow-row gap-ds-space-100">
            <div
                className={twJoin(
                    PROSE_EDITOR_CLASS,
                    "bg-ds-surface overflow-auto",
                )}
            >
                <Milkdown />
            </div>

            {error !== null && (
                <AnankeText size="small" color="color.text.danger">
                    {error}
                </AnankeText>
            )}
        </div>
    );
}

type ToolbarButtonProps = {
    label: string;
    icon: React.ReactNode;
    active?: boolean;
    disabled?: boolean;
    onClick: () => void;
};

function ToolbarButton({label, icon, active = false, disabled = false, onClick}: ToolbarButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={[
                "inline-flex h-8 min-w-8 items-center justify-center rounded-[3px] border px-ds-space-100",
                disabled
                    ? "border-ds-border bg-ds-surface text-ds-text-disabled opacity-50 cursor-not-allowed"
                    : active
                      ? "border-ds-border-accent bg-ds-surface-hovered text-ds-text"
                      : "border-ds-border bg-ds-surface hover:bg-ds-surface-hovered text-ds-text-subtle",
            ].join(" ")}
            title={label}
            aria-label={label}
            aria-pressed={active}
        >
            {icon}
        </button>
    );
}

function ToolbarDivider() {
    return <div className="mx-px h-5 w-px bg-ds-border" />;
}
